import React from 'react';

import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import {
  COLORS,
  FontWeight,
  SANS_SERIF_FONT_FAMILY,
  SERIF_FONT_FAMILY,
  mq,
} from '@clutter/clean';

import starV2Icon from './images/star.svg';

const Container = styled.div`
  background: ${COLORS.cloud};
  position: relative;
  z-index: 2;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${mq({ gap: ['24px', '36px', '48px'] })}
  ${mq({ padding: ['40px 0', '60px 0'] })}
`;

const Title = styled.h2`
  font-family: ${SERIF_FONT_FAMILY};
  font-weight: ${FontWeight.Medium};
  color: ${COLORS.panther};
  text-align: center;
  white-space: pre-line;
  ${mq({
    fontSize: ['28px', '36px', '40px'],
    lineHeight: ['32px', '40px', '44px'],
  })}
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  overflow: hidden;
  width: 100vw;
  align-items: stretch;
`;

const reviewsScrollLeft = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const reviewsScrollRight = keyframes`
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
`;

const Row = styled.div<{ direction: 'left' | 'right'; duration?: number }>`
  display: inline-flex;
  width: max-content;
  ${mq({ gap: ['16px', '20px', '24px'] })}
  animation: ${(p) =>
    p.direction === 'left' ? reviewsScrollLeft : reviewsScrollRight}
    ${(p) => (p.duration ? `${p.duration}s` : '60s')} linear infinite;
  will-change: transform;

  /* Disables animation for users who have reduced motion preferences */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Card = styled.div`
  background: ${COLORS.tealBackground};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${mq({ width: ['280px', '320px', '374px'] })}
`;

const StarRating = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
`;

const StarImage = styled.img`
  width: 16px;
  height: 16px;
`;

const ReviewText = styled.p`
  font-family: ${SANS_SERIF_FONT_FAMILY};
  font-weight: ${FontWeight.Regular};
  font-size: 14px;
  line-height: 19px;
  color: ${COLORS.panther};
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  text-overflow: ellipsis;
`;

const ReviewAuthor = styled.p`
  font-family: ${SANS_SERIF_FONT_FAMILY};
  font-weight: ${FontWeight.Medium};
  font-size: 14px;
  line-height: 19px;
  color: ${COLORS.panther};
  margin: 0;
`;

export type Review = {
  author: string;
  message: string;
  rating: number;
};

const REVIEWS: Review[] = [
  {
    message:
      'Clutter is a great company! My experience was seamless. Half of our belongings went into storage and the other half moved across town.',
    author: 'Olivia M., Seattle, WA',
    rating: 5,
  },
  {
    message:
      'Had the fastest pick-up I’ve seen. They were friendly, easy to work with, and arrived on time. Thank you!',
    author: 'Brian F., Chicago, IL',
    rating: 5,
  },
  {
    message:
      'Their easy online booking and straightforward competitive prices won us over. They make it very easy to manage stuff online!',
    author: 'Isabel M., San Francisco, CA',
    rating: 5,
  },
  {
    message:
      'It truly felt like family was helping me move—so friendly, hardworking, and supportive. Easy and stress-free!',
    author: 'Lauren H., Philadelphia, PA',
    rating: 5,
  },
  {
    message:
      'Phenomenal service! I loved that Clutter provides a tracking system which makes everything super organized and easy to follow.',
    author: 'Robert H., Dallas, TX',
    rating: 5,
  },
  {
    message:
      'Clutter Storage is great and has competitive pricing. The workers were careful with our items and properly protected them for storage.',
    author: 'David K., New York, NY',
    rating: 5,
  },
  {
    message:
      'The Clutter platform itself is super user friendly yet high tech, but the professional movers are what really sets the company apart.',
    author: 'Lisa S., Los Angeles, CA',
    rating: 5,
  },
  {
    message:
      'Clutter has been super helpful making it easy to store and return items when needed. Pick ups and drop offs have been quick and friendly.',
    author: 'Jennifer P., Jersey City, NJ',
    rating: 5,
  },
];

const ReviewCard: React.FC<{
  review: Review;
  ariaHidden?: boolean;
}> = ({ review, ariaHidden }) => (
  <Card aria-hidden={ariaHidden}>
    <StarRating>
      {Array.from({ length: review.rating }).map((_, i) => (
        <StarImage key={i} src={starV2Icon} alt="Star" />
      ))}
    </StarRating>
    <ReviewText>{review.message}</ReviewText>
    <ReviewAuthor>{review.author}</ReviewAuthor>
  </Card>
);

const MarqueeRow: React.FC<{
  reviews: ReadonlyArray<Review>;
  direction: 'left' | 'right';
  duration?: number;
}> = ({ reviews, direction, duration = 60 }) => (
  <Row direction={direction} duration={duration}>
    {reviews.map((review, idx) => (
      <ReviewCard key={`${direction}-a-${idx}`} review={review} />
    ))}
    {reviews.map((review, idx) => (
      <ReviewCard key={`${direction}-b-${idx}`} review={review} />
    ))}
  </Row>
);

export const Reviews: React.FC<{
  title?: string | React.ReactNode;
  reviews?: ReadonlyArray<Review>;
}> = ({ title = 'Loved by people, everywhere', reviews = REVIEWS }) => {
  const chunks: Array<ReadonlyArray<Review>> = [];
  if (reviews.length < 6) {
    chunks.push(reviews);
  } else {
    const midpoint = Math.round(reviews.length / 2);
    chunks.push(reviews.slice(0, midpoint));
    chunks.push(reviews.slice(midpoint));
  }

  return (
    <Container>
      <Section>
        <Title>{title}</Title>
        <Grid>
          {chunks.map((row, index) => (
            <MarqueeRow
              key={index}
              reviews={row}
              direction={index % 2 === 0 ? 'left' : 'right'}
            />
          ))}
        </Grid>
      </Section>
    </Container>
  );
};
